/**
 * GameSimulation - Shared game tick logic for combat and portals.
 *
 * Used by both GameRoom (multiplayer server) and LocalGameServer (offline client).
 * Produces events rather than directly broadcasting or calling callbacks,
 * so each caller can handle I/O in its own way.
 */

import { WorldData } from './WorldData';
import {
    TileType,
    SlashData,
    SLASH_SPAWN_OFFSET,
    SLASH_RATE,
    SLASH_DURATION,
    SWORD_OFFSET,
    SWORD_DAMAGE,
    SLASH_DAMAGE,
    PLAYER_HEIGHT_STANDING,
    PLAYER_HEIGHT_CROUCHING,
    RUNE_STRIKE_COOLDOWN_TICKS,
    SWORD_DESTROY_COOLDOWN_TICKS,
    PORTAL_GRACE_MS,
    MAX_AIR_TICKS,
    DROWNING_DAMAGE_INTERVAL,
    DROWNING_DAMAGE,
    JUMP_HEIGHT,
    isApple,
    isDiggable,
    isFruitTile,
    BASKET_CAPACITY,
    THWOMP_TRIGGER_RANGE,
    COMMAND_BLOCK_COOLDOWN_TICKS,
    QUESTION_BLOCK_FOODS,
    CHUNK_SIZE,
    isShrubTile,
    isIndestructible,
    THORNBUSH_DAMAGE,
    THORNBUSH_DAMAGE_INTERVAL,
    type ToolActionType,
    isFruitDestroyable,
    CORNUCOPIA_FRUITS,
    KILI_KULUPU_HEAL_INTERVAL,
    KILI_LAWA_DAMAGE,
    KILI_LAWA_DAMAGE_INTERVAL,
    KILI_SOWELI_DAMAGE,
    isFruitHazard,
    isSuffocating,
    SUFFOCATION_DAMAGE,
    SUFFOCATION_DAMAGE_INTERVAL,
} from './constants';
import { isPassable, isBodyPassable, isHeadInWater } from './physics';
import { globalPortalRegistry, PortalAddress } from './PortalRegistry';

// ============================================================
// SHARED INTERFACES
// ============================================================

/**
 * Minimal player interface required by game simulation.
 * Both GameRoom's PlayerData and LocalGameServer's LocalPlayerData
 * should satisfy this interface.
 */
export interface SimulationPlayer {
    id: string;
    x: number;
    y: number;
    facing: number;
    isCrouching: boolean;
    hp: number;
    maxHp: number;
    isHoldingSword: boolean;
    swordHoldStartTick: number;
    runeStrikeTicks: Map<string, number>;
    spawnTime: number;
    wasInPortal: boolean;
    lastChopTick: number;  // tick when this player last chopped a trunk (prevents LOG→PLANK double-count)
    lastSwordDestroyTick: number;  // tick when sword last destroyed a tile (3-tick cooldown)
    airTicks: number;              // remaining air ticks (starts at MAX_AIR_TICKS, depletes when head in water)
    drowningTickCounter: number;   // counts ticks since air depleted (resets every DROWNING_DAMAGE_INTERVAL)
    jumpHeight: number;            // current jump height (increased by BOOTS pickup)
    wantsPortalActivation: boolean; // set by UP input when standing on rune, cleared after processing
    equippedItem: number;          // equipment slot (TileType or -1)
    bucketFull: boolean;           // ILO_POKI: bucket contains water
    basketContents: number[];      // ILO_KILI: up to BASKET_CAPACITY fruit TileType values
    isGliding: boolean;            // ILO_WASO: currently gliding (A held in air)
    glideDir: number;              // ILO_WASO: locked facing direction while gliding
    spawnBlockX: number;           // KIWEN_MI: custom respawn X (-1 = default)
    spawnBlockY: number;           // KIWEN_MI: custom respawn Y (-1 = default)
    thornbushTickCounter: number;  // KASI_UTALA: counts ticks in thornbush for damage interval
    fruitHealRemaining: number;    // KILI_KULUPU: remaining slow heal ticks (counts down from 5)
    fruitHealTickCounter: number;  // KILI_KULUPU: tick counter for heal interval
    kiliLawaTickCounter: number;   // KILI_LAWA: damage interval counter
    suffocationTickCounter: number; // Suffocation: tick counter for damage interval when head in solid block
}

/**
 * Active slash projectile tracked by simulation.
 */
export interface ActiveSlash {
    id: number;
    x: number;
    y: number;
    facing: number;
    ticksRemaining: number;
    ownerId: string;
    spawnTick: number;
}

// ============================================================
// SIMULATION EVENTS
// ============================================================

export type SimulationEvent =
    | { type: 'slashSpawned'; slash: SlashData }
    | { type: 'runeUpdated'; x: number; y: number; hieroglyphIndex: number }
    | { type: 'damage'; playerId: string; hp: number; source: 'sword' | 'slash' | 'drowning' }
    | { type: 'death'; playerId: string; deathX: number; deathY: number }
    | { type: 'teleport'; playerId: string; destination: PortalAddress; portalCode: number[] }
    | { type: 'tileChanged'; x: number; y: number; tileType: TileType; inertiaDir?: number }
    | { type: 'pressurePlateChanged'; x: number; y: number; active: boolean }
    | { type: 'commandBlockSpeech'; x: number; y: number; words: number[]; voice: string }
    | { type: 'signUpdated'; x: number; y: number; glyphIndex: number }
    | { type: 'spawnPointSet'; playerId: string; x: number; y: number }
    | { type: 'eyeSwitchActivated'; x: number; y: number; tick: number };

// ============================================================
// GAME SIMULATION
// ============================================================

export class GameSimulation {
    worldData: WorldData;
    activeSlashes: ActiveSlash[] = [];
    private nextSlashId: number = 0;

    constructor(worldData: WorldData) {
        this.worldData = worldData;
    }

    /**
     * Spawn a slash projectile for a player.
     * Checks for rune strikes, solid tiles, and player hits at spawn position.
     */
    spawnSlashForPlayer(
        player: SimulationPlayer,
        currentTick: number,
        allPlayers: SimulationPlayer[] = [],
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        const swordX = player.x + (player.facing * SWORD_OFFSET);
        const swordY = player.y + (player.isCrouching ? 0 : 1);
        const swordTile = this.worldData.getTile(swordX, swordY);

        // Sword is inside a solid block: no slash
        if (!isBodyPassable(swordTile) && swordTile !== TileType.RUNE) {
            return events;
        }

        const spawnX = player.x + (player.facing * SLASH_SPAWN_OFFSET);
        const spawnY = swordY;
        const tile = this.worldData.getTile(spawnX, spawnY);

        // Rune at spawn position: cycle hieroglyph, no slash
        if (tile === TileType.RUNE) {
            const newIndex = this.worldData.cycleRuneHieroglyph(spawnX, spawnY);
            events.push({ type: 'runeUpdated', x: spawnX, y: spawnY, hieroglyphIndex: newIndex });
            return events;
        }

        // Ladder or leaves at spawn position: convert to stick, no slash
        if (tile === TileType.LADDER || tile === TileType.LEAVES) {
            this.worldData.setTile(spawnX, spawnY, TileType.STICK);
            this.worldData.addFallingBlock(spawnX, spawnY);
            events.push({ type: 'tileChanged', x: spawnX, y: spawnY, tileType: TileType.STICK });
            return events;
        }

        // Stick at spawn position: destroy completely, no slash
        if (tile === TileType.STICK) {
            this.worldData.setTile(spawnX, spawnY, TileType.SKY);
            this.worldData.removeFallingBlock(spawnX, spawnY);
            this.worldData.propagateInstabilityUp(spawnX, spawnY - 1);
            events.push({ type: 'tileChanged', x: spawnX, y: spawnY, tileType: TileType.SKY });
            return events;
        }

        // Trunk at spawn position: chop to falling logs, no slash
        if (tile === TileType.TRUNK) {
            events.push(...this.chopTrunk(spawnX, spawnY, player.facing));
            return events;
        }

        // LOG at spawn position: convert to plank, no slash
        if (tile === TileType.LOG) {
            this.worldData.setTile(spawnX, spawnY, TileType.PLANK);
            this.worldData.removeFallingBlock(spawnX, spawnY);
            events.push({ type: 'tileChanged', x: spawnX, y: spawnY, tileType: TileType.PLANK });
            return events;
        }

        // Cornucopia at spawn position: split into random fruits, no slash
        if (tile === TileType.KILI_ALE) {
            events.push(...this.splitCornucopia(spawnX, spawnY));
            return events;
        }

        // Destroyable fruit at spawn position: destroy, no slash
        if (isFruitDestroyable(tile)) {
            this.worldData.setTile(spawnX, spawnY, TileType.SKY);
            this.worldData.removeFallingBlock(spawnX, spawnY);
            this.worldData.propagateInstabilityUp(spawnX, spawnY - 1);
            events.push({ type: 'tileChanged', x: spawnX, y: spawnY, tileType: TileType.SKY });
            return events;
        }

        // Solid tile: no slash
        if (!isBodyPassable(tile)) {
            return events;
        }

        // Check for player hits at spawn position
        for (const target of allPlayers) {
            if (target.id === player.id || target.hp <= 0) continue;
            if (this.overlapsPlayer(spawnX, spawnY, target)) {
                target.hp -= SLASH_DAMAGE;
                events.push({ type: 'damage', playerId: target.id, hp: target.hp, source: 'slash' });
                if (target.hp <= 0) {
                    events.push({ type: 'death', playerId: target.id, deathX: target.x, deathY: target.y + 1 });
                }
            }
        }

        // Create slash
        const slash: ActiveSlash = {
            id: this.nextSlashId++,
            x: spawnX,
            y: spawnY,
            facing: player.facing,
            ticksRemaining: SLASH_DURATION,
            ownerId: player.id,
            spawnTick: currentTick,
        };
        this.activeSlashes.push(slash);

        events.push({
            type: 'slashSpawned',
            slash: {
                id: slash.id,
                spawnX: slash.x,
                spawnY: slash.y,
                facing: slash.facing,
                spawnTick: slash.spawnTick,
                duration: SLASH_DURATION,
                ownerId: slash.ownerId,
            },
        });

        return events;
    }

    /**
     * Process one combat tick for all players.
     *
     * @param players All players to simulate
     * @param currentTick Current global tick number
     * @param skipFirstTickSpawn If true, skip slash spawn on tick 0 of sword hold
     *   (GameRoom spawns immediately on the swordHold message handler)
     */
    tickCombat(
        players: SimulationPlayer[],
        currentTick: number,
        skipFirstTickSpawn: boolean = false,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        // 1. SPAWN SLASHES (skip club holders — they use melee-only via tickClubMelee)
        for (const player of players) {
            if (!player.isHoldingSword || player.hp <= 0) continue;
            if (player.equippedItem === TileType.ILO_IJO) continue; // Club: no slashes

            const ticksHeld = currentTick - player.swordHoldStartTick;
            if (skipFirstTickSpawn && ticksHeld === 0) continue;
            if (ticksHeld % SLASH_RATE !== 0) continue;

            events.push(...this.spawnSlashForPlayer(player, currentTick, players));
        }

        // 2. PROCESS EXISTING SLASHES (move + collision)
        const expiredIds: number[] = [];

        for (const slash of this.activeSlashes) {
            slash.ticksRemaining--;

            if (slash.ticksRemaining > 0) {
                const nextX = slash.x + slash.facing;
                const tile = this.worldData.getTile(nextX, slash.y);

                // Ladder or leaves hit: convert to stick and expire slash
                if (tile === TileType.LADDER || tile === TileType.LEAVES) {
                    this.worldData.setTile(nextX, slash.y, TileType.STICK);
                    this.worldData.addFallingBlock(nextX, slash.y);
                    events.push({ type: 'tileChanged', x: nextX, y: slash.y, tileType: TileType.STICK });
                    expiredIds.push(slash.id);
                    continue;
                }

                // Stick hit: destroy completely and expire slash
                if (tile === TileType.STICK) {
                    this.worldData.setTile(nextX, slash.y, TileType.SKY);
                    this.worldData.removeFallingBlock(nextX, slash.y);
                    this.worldData.propagateInstabilityUp(nextX, slash.y - 1);
                    events.push({ type: 'tileChanged', x: nextX, y: slash.y, tileType: TileType.SKY });
                    expiredIds.push(slash.id);
                    continue;
                }

                // Trunk hit: chop to falling logs and expire slash
                if (tile === TileType.TRUNK) {
                    events.push(...this.chopTrunk(nextX, slash.y, slash.facing));
                    expiredIds.push(slash.id);
                    continue;
                }

                // LOG hit: convert to plank and expire slash
                if (tile === TileType.LOG) {
                    this.worldData.setTile(nextX, slash.y, TileType.PLANK);
                    this.worldData.removeFallingBlock(nextX, slash.y);
                    events.push({ type: 'tileChanged', x: nextX, y: slash.y, tileType: TileType.PLANK });
                    expiredIds.push(slash.id);
                    continue;
                }

                // Cornucopia hit: split into random fruits and expire slash
                if (tile === TileType.KILI_ALE) {
                    events.push(...this.splitCornucopia(nextX, slash.y));
                    expiredIds.push(slash.id);
                    continue;
                }

                // Destroyable fruit hit: destroy and expire slash
                if (isFruitDestroyable(tile)) {
                    this.worldData.setTile(nextX, slash.y, TileType.SKY);
                    this.worldData.removeFallingBlock(nextX, slash.y);
                    this.worldData.propagateInstabilityUp(nextX, slash.y - 1);
                    events.push({ type: 'tileChanged', x: nextX, y: slash.y, tileType: TileType.SKY });
                    expiredIds.push(slash.id);
                    continue;
                }

                // Eye switch: activate on projectile hit
                if (tile === TileType.KIWEN_LUKIN) {
                    this.worldData.activateEyeSwitch(nextX, slash.y, currentTick);
                    events.push({ type: 'eyeSwitchActivated', x: nextX, y: slash.y, tick: currentTick });
                    expiredIds.push(slash.id);
                    continue;
                }

                // Shrub: destroy on slash hit
                if (isShrubTile(tile)) {
                    this.worldData.setTile(nextX, slash.y, TileType.SKY);
                    events.push({ type: 'tileChanged', x: nextX, y: slash.y, tileType: TileType.SKY });
                    // Don't expire slash — it passes through
                }

                // Vine: same as ladder — convert to stick on slash hit
                if (tile === TileType.KASI_LINJA) {
                    this.worldData.setTile(nextX, slash.y, TileType.STICK);
                    this.worldData.addFallingBlock(nextX, slash.y);
                    events.push({ type: 'tileChanged', x: nextX, y: slash.y, tileType: TileType.STICK });
                    expiredIds.push(slash.id);
                    continue;
                }

                if (!isBodyPassable(tile)) {
                    if (tile === TileType.RUNE) {
                        const newIndex = this.worldData.cycleRuneHieroglyph(nextX, slash.y);
                        events.push({ type: 'runeUpdated', x: nextX, y: slash.y, hieroglyphIndex: newIndex });
                    }
                    expiredIds.push(slash.id);
                    continue;
                }

                slash.x = nextX;

                // Check player hit at new position
                for (const target of players) {
                    if (target.id === slash.ownerId || target.hp <= 0) continue;
                    if (this.overlapsPlayer(slash.x, slash.y, target)) {
                        target.hp -= SLASH_DAMAGE;
                        events.push({ type: 'damage', playerId: target.id, hp: target.hp, source: 'slash' });
                        if (target.hp <= 0) {
                            events.push({ type: 'death', playerId: target.id, deathX: target.x, deathY: target.y + 1 });
                        }
                    }
                }
            } else {
                expiredIds.push(slash.id);
            }
        }

        if (expiredIds.length > 0) {
            this.activeSlashes = this.activeSlashes.filter(s => !expiredIds.includes(s.id));
        }

        // 3. SWORD DAMAGE (continuous, per tick) — skip club holders
        for (const player of players) {
            if (!player.isHoldingSword || player.hp <= 0) continue;
            if (player.equippedItem === TileType.ILO_IJO) continue; // Club handled separately

            const swordX = player.x + (player.facing * SWORD_OFFSET);
            const swordTileY = player.y + (player.isCrouching ? 0 : 1); // Feet level (matches slash spawn Y)

            // Damage other players
            for (const target of players) {
                if (target.id === player.id || target.hp <= 0) continue;
                if (this.overlapsPlayer(swordX, swordTileY, target)) {
                    target.hp -= SWORD_DAMAGE;
                    events.push({ type: 'damage', playerId: target.id, hp: target.hp, source: 'sword' });
                    if (target.hp <= 0) {
                        events.push({ type: 'death', playerId: target.id, deathX: target.x, deathY: target.y + 1 });
                    }
                }
            }

            // Strike rune (3-tick cooldown) — sword is 1 tile at feet level
            const runeKey = `${swordX},${swordTileY}`;
            const tile = this.worldData.getTile(swordX, swordTileY);
            if (tile === TileType.RUNE) {
                const lastStrikeTick = player.runeStrikeTicks.get(runeKey) ?? -999;
                if (currentTick - lastStrikeTick >= RUNE_STRIKE_COOLDOWN_TICKS) {
                    player.runeStrikeTicks.set(runeKey, currentTick);
                    const newIndex = this.worldData.cycleRuneHieroglyph(swordX, swordTileY);
                    events.push({ type: 'runeUpdated', x: swordX, y: swordTileY, hieroglyphIndex: newIndex });
                }
            }

            // Sword tile destruction — 3-tick cooldown
            const canDestroy = currentTick - player.lastSwordDestroyTick >= SWORD_DESTROY_COOLDOWN_TICKS;

            // Sword hitting ladder or leaves: convert to stick (falls)
            if (canDestroy && (tile === TileType.LADDER || tile === TileType.LEAVES)) {
                this.worldData.setTile(swordX, swordTileY, TileType.STICK);
                this.worldData.addFallingBlock(swordX, swordTileY);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.STICK });
                player.lastSwordDestroyTick = currentTick;
            }

            // Sword hitting stick: destroy completely
            if (canDestroy && tile === TileType.STICK) {
                this.worldData.setTile(swordX, swordTileY, TileType.SKY);
                this.worldData.removeFallingBlock(swordX, swordTileY);
                this.worldData.propagateInstabilityUp(swordX, swordTileY - 1);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
                player.lastSwordDestroyTick = currentTick;
            }

            // Sword hitting trunk: chop to falling logs
            if (tile === TileType.TRUNK) {
                player.lastChopTick = currentTick;
                events.push(...this.chopTrunk(swordX, swordTileY, player.facing));
            }

            // Sword hitting LOG: convert to plank (skip if this player just chopped a trunk)
            if (canDestroy && tile === TileType.LOG && currentTick - player.lastChopTick > 1) {
                this.worldData.setTile(swordX, swordTileY, TileType.PLANK);
                this.worldData.removeFallingBlock(swordX, swordTileY);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.PLANK });
                player.lastSwordDestroyTick = currentTick;
            }

            // Sword hitting eye switch: activate for 10 seconds
            if (tile === TileType.KIWEN_LUKIN) {
                this.worldData.activateEyeSwitch(swordX, swordTileY, currentTick);
                events.push({ type: 'eyeSwitchActivated', x: swordX, y: swordTileY, tick: currentTick });
            }

            // Sword hitting shrub: destroy
            if (isShrubTile(tile)) {
                this.worldData.setTile(swordX, swordTileY, TileType.SKY);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
            }

            // Sword hitting vine: convert to stick (same as ladder)
            if (canDestroy && tile === TileType.KASI_LINJA) {
                this.worldData.setTile(swordX, swordTileY, TileType.STICK);
                this.worldData.addFallingBlock(swordX, swordTileY);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.STICK });
                player.lastSwordDestroyTick = currentTick;
            }

            // Sword hitting cornucopia: split into fruits
            if (tile === TileType.KILI_ALE) {
                events.push(...this.splitCornucopia(swordX, swordTileY));
                player.lastSwordDestroyTick = currentTick;
            }

            // Sword hitting destroyable fruit: destroy
            if (canDestroy && isFruitDestroyable(tile)) {
                this.worldData.setTile(swordX, swordTileY, TileType.SKY);
                this.worldData.removeFallingBlock(swordX, swordTileY);
                this.worldData.propagateInstabilityUp(swordX, swordTileY - 1);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
                player.lastSwordDestroyTick = currentTick;
            }
        }

        return events;
    }

    /**
     * Immediate sword tile check (called on swordHold start).
     * Ensures even a quick tap processes the sword's tile interaction at x±1.
     * tickCombat handles continuous checks while held, but a fast release
     * can arrive before the next tick — this covers that gap.
     */
    checkSwordTile(player: SimulationPlayer, currentTick: number): SimulationEvent[] {
        const events: SimulationEvent[] = [];
        const swordX = player.x + (player.facing * SWORD_OFFSET);
        const swordTileY = player.y + (player.isCrouching ? 0 : 1);
        const tile = this.worldData.getTile(swordX, swordTileY);

        if (tile === TileType.RUNE) {
            const runeKey = `${swordX},${swordTileY}`;
            const lastStrikeTick = player.runeStrikeTicks.get(runeKey) ?? -999;
            if (currentTick - lastStrikeTick >= RUNE_STRIKE_COOLDOWN_TICKS) {
                player.runeStrikeTicks.set(runeKey, currentTick);
                const newIndex = this.worldData.cycleRuneHieroglyph(swordX, swordTileY);
                events.push({ type: 'runeUpdated', x: swordX, y: swordTileY, hieroglyphIndex: newIndex });
            }
        }

        // Sword tile destruction — 3-tick cooldown prevents rapid double-destroy
        // (e.g. LADDER→STICK on tick 0, then STICK→SKY must wait until tick 3)
        const canDestroy = currentTick - player.lastSwordDestroyTick >= SWORD_DESTROY_COOLDOWN_TICKS;

        if (canDestroy && (tile === TileType.LADDER || tile === TileType.LEAVES)) {
            this.worldData.setTile(swordX, swordTileY, TileType.STICK);
            this.worldData.addFallingBlock(swordX, swordTileY);
            events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.STICK });
            player.lastSwordDestroyTick = currentTick;
        }

        if (canDestroy && tile === TileType.STICK) {
            this.worldData.setTile(swordX, swordTileY, TileType.SKY);
            this.worldData.removeFallingBlock(swordX, swordTileY);
            this.worldData.propagateInstabilityUp(swordX, swordTileY - 1);
            events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
            player.lastSwordDestroyTick = currentTick;
        }

        if (tile === TileType.TRUNK) {
            player.lastChopTick = currentTick;
            events.push(...this.chopTrunk(swordX, swordTileY, player.facing));
        }

        if (canDestroy && tile === TileType.LOG && currentTick - player.lastChopTick > 1) {
            this.worldData.setTile(swordX, swordTileY, TileType.PLANK);
            this.worldData.removeFallingBlock(swordX, swordTileY);
            events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.PLANK });
            player.lastSwordDestroyTick = currentTick;
        }

        // Eye switch at sword position
        if (tile === TileType.KIWEN_LUKIN) {
            this.worldData.activateEyeSwitch(swordX, swordTileY, currentTick);
            events.push({ type: 'eyeSwitchActivated', x: swordX, y: swordTileY, tick: currentTick });
        }

        // Shrub: destroy on sword tap
        if (isShrubTile(tile)) {
            this.worldData.setTile(swordX, swordTileY, TileType.SKY);
            events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
        }

        // Vine: convert to stick on sword tap (same as ladder)
        if (canDestroy && tile === TileType.KASI_LINJA) {
            this.worldData.setTile(swordX, swordTileY, TileType.STICK);
            this.worldData.addFallingBlock(swordX, swordTileY);
            events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.STICK });
            player.lastSwordDestroyTick = currentTick;
        }

        // Cornucopia: split on sword tap
        if (tile === TileType.KILI_ALE) {
            events.push(...this.splitCornucopia(swordX, swordTileY));
            player.lastSwordDestroyTick = currentTick;
        }

        // Destroyable fruit: destroy on sword tap
        if (canDestroy && isFruitDestroyable(tile)) {
            this.worldData.setTile(swordX, swordTileY, TileType.SKY);
            this.worldData.removeFallingBlock(swordX, swordTileY);
            this.worldData.propagateInstabilityUp(swordX, swordTileY - 1);
            events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
            player.lastSwordDestroyTick = currentTick;
        }

        return events;
    }

    /**
     * Process portal detection for all players.
     * Returns teleport events; callers handle actual teleportation I/O.
     */
    tickPortals(players: SimulationPlayer[]): SimulationEvent[] {
        const events: SimulationEvent[] = [];
        const now = Date.now();

        for (const player of players) {
            // Only process when player explicitly requests portal activation (UP on rune)
            if (!player.wantsPortalActivation) continue;
            player.wantsPortalActivation = false; // consume the flag

            const timeSinceSpawn = now - player.spawnTime;
            if (timeSinceSpawn < PORTAL_GRACE_MS) continue;

            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            const feetY = player.y + height - 1;

            // Player must be standing directly on a rune tile
            const tileUnderFeet = this.worldData.getTile(player.x, feetY + 1);
            if (tileUnderFeet !== TileType.RUNE) continue;

            const runeY = feetY + 1;
            const topmostRuneY = this.worldData.findTopmostRune(player.x, runeY);
            if (topmostRuneY === null) continue;

            if (!this.worldData.isPortalOpen(player.x, topmostRuneY)) continue;

            const portalCode = this.worldData.getPortalCode(player.x, topmostRuneY);
            if (portalCode.length === 0) continue;

            const destination = globalPortalRegistry.lookup(portalCode);
            if (!destination) continue;

            events.push({
                type: 'teleport',
                playerId: player.id,
                destination,
                portalCode,
            });
        }

        return events;
    }

    /**
     * Process breathing for all players.
     * Depletes air when head is in WATER; restores instantly when head is not in water.
     * Once air is depleted, deals drowning damage every DROWNING_DAMAGE_INTERVAL ticks.
     */
    tickBreathing(
        players: SimulationPlayer[],
        getTile: (x: number, y: number) => TileType,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;

            const headUnderwater = isHeadInWater(player.x, player.y, getTile);

            if (headUnderwater) {
                if (player.airTicks > 0) {
                    player.airTicks--;
                } else {
                    // Air depleted — drowning damage
                    player.drowningTickCounter++;
                    if (player.drowningTickCounter >= DROWNING_DAMAGE_INTERVAL) {
                        player.drowningTickCounter = 0;
                        player.hp -= DROWNING_DAMAGE;
                        events.push({ type: 'damage', playerId: player.id, hp: player.hp, source: 'drowning' });
                        if (player.hp <= 0) {
                            events.push({ type: 'death', playerId: player.id, deathX: player.x, deathY: player.y + 1 });
                        }
                    }
                }
            } else {
                // Head above water — fully restore air
                player.airTicks = MAX_AIR_TICKS;
                player.drowningTickCounter = 0;
            }
        }

        return events;
    }

    /**
     * Chop a trunk: convert TRUNK to LOG (falling) at (x, y) and up to 5 trunks above.
     * Then evaporate up to 8 leaves above the topmost chopped trunk into falling sticks,
     * and check connected branches for remaining trunk support.
     * All created falling blocks use justAdded so they wait 1 tick before moving.
     */
    private chopTrunk(x: number, y: number, attackDir?: number): SimulationEvent[] {
        const events: SimulationEvent[] = [];
        let topmostY = y;
        for (let dy = 0; dy <= 5; dy++) {
            const ty = y - dy;
            if (this.worldData.getTile(x, ty) !== TileType.TRUNK) break;
            this.worldData.setTile(x, ty, TileType.LOG);
            this.worldData.addFallingBlock(x, ty, attackDir, true);
            events.push({ type: 'tileChanged', x, y: ty, tileType: TileType.LOG, inertiaDir: attackDir });
            topmostY = ty;
        }

        // Check up to 8 leaves above the topmost chopped trunk.
        // Only evaporate leaves that have no trunk support from either side
        // (L-shaped search: 3 tiles out horizontally, then down through leaves for TRUNK).
        const evaporatedRows: number[] = [];
        for (let dy = 1; dy <= 8; dy++) {
            const ly = topmostY - dy;
            if (this.worldData.getTile(x, ly) !== TileType.LEAVES) break;
            const hasSupport =
                this.worldData.hasTrunkSupportInDirection(x, ly, 1) ||
                this.worldData.hasTrunkSupportInDirection(x, ly, -1);
            if (!hasSupport) {
                const evapType = isApple(x, ly) ? TileType.HEART_CONTAINER : TileType.STICK;
                this.worldData.setTile(x, ly, evapType);
                this.worldData.addFallingBlock(x, ly, undefined, true);
                events.push({ type: 'tileChanged', x, y: ly, tileType: evapType });
                evaporatedRows.push(ly);
            }
        }

        // For evaporated rows, also check connected branches for support
        if (evaporatedRows.length > 0) {
            this.worldData.evaporateUnsupportedBranches(x, evaporatedRows, true);
            for (const rowY of evaporatedRows) {
                for (const dir of [1, -1]) {
                    for (let dx = 1; dx <= 3; dx++) {
                        const lx = x + dx * dir;
                        const tile = this.worldData.getTile(lx, rowY);
                        if (tile === TileType.STICK || tile === TileType.HEART_CONTAINER) {
                            events.push({ type: 'tileChanged', x: lx, y: rowY, tileType: tile });
                        } else {
                            break;
                        }
                    }
                }
            }
        }

        // Second pass: after branches evaporate, check for leaves above them that
        // lost support. The first pass only checks the center column; branch evaporation
        // can leave upper-layer leaves orphaned (e.g. narrow top-of-canopy rows whose
        // only support was the wider layer below, now turned to sticks).
        // Scan all columns that had any evaporation and re-check upward.
        const allEvaporatedCols = new Set<number>();
        allEvaporatedCols.add(x);
        for (const rowY of evaporatedRows) {
            for (const dir of [1, -1]) {
                for (let dx = 1; dx <= 3; dx++) {
                    const lx = x + dx * dir;
                    const t = this.worldData.getTile(lx, rowY);
                    if (t === TileType.STICK || t === TileType.HEART_CONTAINER) {
                        allEvaporatedCols.add(lx);
                    } else {
                        break;
                    }
                }
            }
        }

        for (const col of allEvaporatedCols) {
            // Find the topmost evaporated row in this column
            let topEvaporatedY = Infinity;
            for (const rowY of evaporatedRows) {
                if (rowY < topEvaporatedY) topEvaporatedY = rowY;
            }
            // Also check branch columns — find the highest evaporated tile we placed
            for (let checkY = topmostY - 8; checkY <= topmostY; checkY++) {
                const t = this.worldData.getTile(col, checkY);
                if (t === TileType.STICK || t === TileType.HEART_CONTAINER) {
                    if (checkY < topEvaporatedY) topEvaporatedY = checkY;
                    break;
                }
            }

            // Scan upward from the topmost evaporation looking for orphaned leaves
            const secondPassRows: number[] = [];
            for (let dy = 1; dy <= 8; dy++) {
                const ly = topEvaporatedY - dy;
                const tile = this.worldData.getTile(col, ly);
                if (tile !== TileType.LEAVES) break;
                const hasSupport =
                    this.worldData.hasTrunkSupportInDirection(col, ly, 1) ||
                    this.worldData.hasTrunkSupportInDirection(col, ly, -1);
                if (!hasSupport) {
                    const evapType = isApple(col, ly) ? TileType.HEART_CONTAINER : TileType.STICK;
                    this.worldData.setTile(col, ly, evapType);
                    this.worldData.addFallingBlock(col, ly, undefined, true);
                    events.push({ type: 'tileChanged', x: col, y: ly, tileType: evapType });
                    secondPassRows.push(ly);
                }
            }
            if (secondPassRows.length > 0) {
                this.worldData.evaporateUnsupportedBranches(col, secondPassRows, true);
                for (const rowY of secondPassRows) {
                    for (const dir of [1, -1]) {
                        for (let dx = 1; dx <= 3; dx++) {
                            const lx = col + dx * dir;
                            const tile = this.worldData.getTile(lx, rowY);
                            if (tile === TileType.STICK || tile === TileType.HEART_CONTAINER) {
                                events.push({ type: 'tileChanged', x: lx, y: rowY, tileType: tile });
                            } else {
                                break;
                            }
                        }
                    }
                }
            }
        }

        return events;
    }

    /**
     * Check all players for overlap with BOOTS tiles.
     * When a player's body occupies a BOOTS tile, consume it:
     * set tile to SKY, grant jumpHeight = JUMP_HEIGHT.
     */
    tickBoots(
        players: SimulationPlayer[],
        getTile: (x: number, y: number) => TileType,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;

            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            for (let dy = 0; dy < height; dy++) {
                const checkY = player.y + dy;
                if (getTile(player.x, checkY) === TileType.BOOTS) {
                    this.worldData.setTile(player.x, checkY, TileType.SKY);
                    player.jumpHeight = JUMP_HEIGHT;
                    events.push({ type: 'tileChanged', x: player.x, y: checkY, tileType: TileType.SKY });
                    break;
                }
            }
        }

        return events;
    }

    /**
     * Check all players for overlap with YELLOW_KEY tiles.
     * When picked up: consume the key, convert all YELLOW_DOOR → YELLOW_DOOR_OPEN.
     */
    tickYellowKey(
        players: SimulationPlayer[],
        getTile: (x: number, y: number) => TileType,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;

            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            for (let dy = 0; dy < height; dy++) {
                const checkY = player.y + dy;
                if (getTile(player.x, checkY) === TileType.YELLOW_KEY) {
                    // Consume key
                    this.worldData.setTile(player.x, checkY, TileType.SKY);
                    events.push({ type: 'tileChanged', x: player.x, y: checkY, tileType: TileType.SKY });

                    // Unlock all yellow doors in the world
                    this.worldData.forEachChunk((cx, cy, tiles) => {
                        for (let row = 0; row < tiles.length; row++) {
                            for (let col = 0; col < tiles[row].length; col++) {
                                if (tiles[row][col] === TileType.YELLOW_DOOR) {
                                    const doorX = cx * 8 + col;
                                    const doorY = cy * 8 + row;
                                    this.worldData.setTile(doorX, doorY, TileType.YELLOW_DOOR_OPEN);
                                    events.push({ type: 'tileChanged', x: doorX, y: doorY, tileType: TileType.YELLOW_DOOR_OPEN });
                                }
                            }
                        }
                    });
                    return events; // One key unlocks all doors at once
                }
            }
        }

        return events;
    }

    /**
     * Process a tool action for a player.
     * Returns events + whether the action succeeded.
     */
    processToolAction(
        player: SimulationPlayer,
        action: ToolActionType,
        allPlayers: SimulationPlayer[],
        currentTick: number,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];
        const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        const feetY = player.y + height - 1;

        switch (action) {
            case 'shovel_dig': {
                // Dig the tile below player's feet onto head
                const belowY = feetY + 1;
                const belowTile = this.worldData.getTile(player.x, belowY);
                if (isDiggable(belowTile)) {
                    this.worldData.setTile(player.x, belowY, TileType.SKY);
                    this.worldData.propagateInstabilityUp(player.x, belowY - 1);
                    events.push({ type: 'tileChanged', x: player.x, y: belowY, tileType: TileType.SKY });
                }
                break;
            }

            case 'drill_front': {
                // Disintegrate block in front at feet level
                const frontX = player.x + player.facing;
                const frontTile = this.worldData.getTile(frontX, feetY);
                if (frontTile !== TileType.SKY && frontTile !== TileType.PORTAL && frontTile !== TileType.WATER && frontTile !== TileType.WATER_SURFACE && frontTile !== TileType.RUNE) {
                    this.worldData.setTile(frontX, feetY, TileType.SKY);
                    this.worldData.removeFallingBlock(frontX, feetY);
                    this.worldData.propagateInstabilityUp(frontX, feetY - 1);
                    events.push({ type: 'tileChanged', x: frontX, y: feetY, tileType: TileType.SKY });
                }
                break;
            }

            case 'drill_below': {
                // Disintegrate block below player
                const belowY = feetY + 1;
                const belowTile = this.worldData.getTile(player.x, belowY);
                if (belowTile !== TileType.SKY && belowTile !== TileType.PORTAL && belowTile !== TileType.WATER && belowTile !== TileType.WATER_SURFACE && belowTile !== TileType.RUNE) {
                    this.worldData.setTile(player.x, belowY, TileType.SKY);
                    this.worldData.removeFallingBlock(player.x, belowY);
                    events.push({ type: 'tileChanged', x: player.x, y: belowY, tileType: TileType.SKY });
                }
                break;
            }

            case 'plunger_pull': {
                // Pull block from 2 tiles in front to 1 tile in front
                const pullFromX = player.x + player.facing * 2;
                const pullToX = player.x + player.facing;
                const pullTile = this.worldData.getTile(pullFromX, feetY);
                const destTile = this.worldData.getTile(pullToX, feetY);
                if (pullTile !== TileType.SKY && pullTile !== TileType.PORTAL && pullTile !== TileType.WATER && pullTile !== TileType.WATER_SURFACE && pullTile !== TileType.RUNE
                    && destTile === TileType.SKY) {
                    this.worldData.setTile(pullFromX, feetY, TileType.SKY);
                    this.worldData.removeFallingBlock(pullFromX, feetY);
                    this.worldData.propagateInstabilityUp(pullFromX, feetY - 1);
                    this.worldData.setTile(pullToX, feetY, pullTile);
                    events.push({ type: 'tileChanged', x: pullFromX, y: feetY, tileType: TileType.SKY });
                    events.push({ type: 'tileChanged', x: pullToX, y: feetY, tileType: pullTile });
                }
                break;
            }

            case 'bucket_fill': {
                // Pick up water from in front
                const frontX = player.x + player.facing;
                const frontTile = this.worldData.getTile(frontX, feetY);
                if ((frontTile === TileType.WATER || frontTile === TileType.WATER_SURFACE) && !player.bucketFull) {
                    this.worldData.setTile(frontX, feetY, TileType.SKY);
                    player.bucketFull = true;
                    events.push({ type: 'tileChanged', x: frontX, y: feetY, tileType: TileType.SKY });
                }
                break;
            }

            case 'bucket_pour': {
                // Place water in front
                const frontX = player.x + player.facing;
                const frontTile = this.worldData.getTile(frontX, feetY);
                if (frontTile === TileType.SKY && player.bucketFull) {
                    this.worldData.setTile(frontX, feetY, TileType.WATER);
                    player.bucketFull = false;
                    events.push({ type: 'tileChanged', x: frontX, y: feetY, tileType: TileType.WATER });
                }
                break;
            }

            case 'basket_collect': {
                // Collect fruit from in front into basket
                const frontX = player.x + player.facing;
                const frontTile = this.worldData.getTile(frontX, feetY);
                if (isFruitTile(frontTile) && player.basketContents.length < BASKET_CAPACITY) {
                    this.worldData.setTile(frontX, feetY, TileType.SKY);
                    this.worldData.removeFallingBlock(frontX, feetY);
                    this.worldData.propagateInstabilityUp(frontX, feetY - 1);
                    player.basketContents.push(frontTile);
                    events.push({ type: 'tileChanged', x: frontX, y: feetY, tileType: TileType.SKY });
                }
                break;
            }

            case 'basket_place': {
                // Place last fruit from basket onto empty tile in front
                const frontX = player.x + player.facing;
                const frontTile = this.worldData.getTile(frontX, feetY);
                if (frontTile === TileType.SKY && player.basketContents.length > 0) {
                    const fruit = player.basketContents.pop()!;
                    this.worldData.setTile(frontX, feetY, fruit);
                    events.push({ type: 'tileChanged', x: frontX, y: feetY, tileType: fruit });
                }
                break;
            }

            case 'dice_place': {
                // Handled by blockAction with face value metadata, not here
                break;
            }
        }

        return events;
    }

    /**
     * Club (ILO_IJO) melee tick: same as sword melee damage but NO slash spawning.
     * Also handles RUNE cycling and tile destruction at melee range.
     */
    tickClubMelee(
        players: SimulationPlayer[],
        currentTick: number,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (!player.isHoldingSword || player.hp <= 0) continue;
            if (player.equippedItem !== TileType.ILO_IJO) continue;

            const swordX = player.x + (player.facing * SWORD_OFFSET);
            const swordTileY = player.y + (player.isCrouching ? 0 : 1);

            // Damage other players (melee only)
            for (const target of players) {
                if (target.id === player.id || target.hp <= 0) continue;
                if (this.overlapsPlayer(swordX, swordTileY, target)) {
                    target.hp -= SWORD_DAMAGE;
                    events.push({ type: 'damage', playerId: target.id, hp: target.hp, source: 'sword' });
                    if (target.hp <= 0) {
                        events.push({ type: 'death', playerId: target.id, deathX: target.x, deathY: target.y + 1 });
                    }
                }
            }

            // Strike rune
            const runeKey = `${swordX},${swordTileY}`;
            const tile = this.worldData.getTile(swordX, swordTileY);
            if (tile === TileType.RUNE) {
                const lastStrikeTick = player.runeStrikeTicks.get(runeKey) ?? -999;
                if (currentTick - lastStrikeTick >= RUNE_STRIKE_COOLDOWN_TICKS) {
                    player.runeStrikeTicks.set(runeKey, currentTick);
                    const newIndex = this.worldData.cycleRuneHieroglyph(swordX, swordTileY);
                    events.push({ type: 'runeUpdated', x: swordX, y: swordTileY, hieroglyphIndex: newIndex });
                }
            }

            // Tile destruction (same 3-tick cooldown)
            const canDestroy = currentTick - player.lastSwordDestroyTick >= SWORD_DESTROY_COOLDOWN_TICKS;

            if (canDestroy && (tile === TileType.LADDER || tile === TileType.LEAVES)) {
                this.worldData.setTile(swordX, swordTileY, TileType.STICK);
                this.worldData.addFallingBlock(swordX, swordTileY);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.STICK });
                player.lastSwordDestroyTick = currentTick;
            }

            if (canDestroy && tile === TileType.STICK) {
                this.worldData.setTile(swordX, swordTileY, TileType.SKY);
                this.worldData.removeFallingBlock(swordX, swordTileY);
                this.worldData.propagateInstabilityUp(swordX, swordTileY - 1);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
                player.lastSwordDestroyTick = currentTick;
            }

            if (tile === TileType.TRUNK) {
                player.lastChopTick = currentTick;
                events.push(...this.chopTrunk(swordX, swordTileY, player.facing));
            }

            if (canDestroy && tile === TileType.LOG && currentTick - player.lastChopTick > 1) {
                this.worldData.setTile(swordX, swordTileY, TileType.PLANK);
                this.worldData.removeFallingBlock(swordX, swordTileY);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.PLANK });
                player.lastSwordDestroyTick = currentTick;
            }

            // Club hitting cornucopia: split
            if (tile === TileType.KILI_ALE) {
                events.push(...this.splitCornucopia(swordX, swordTileY));
                player.lastSwordDestroyTick = currentTick;
            }

            // Club hitting destroyable fruit: destroy
            if (canDestroy && isFruitDestroyable(tile)) {
                this.worldData.setTile(swordX, swordTileY, TileType.SKY);
                this.worldData.removeFallingBlock(swordX, swordTileY);
                this.worldData.propagateInstabilityUp(swordX, swordTileY - 1);
                events.push({ type: 'tileChanged', x: swordX, y: swordTileY, tileType: TileType.SKY });
                player.lastSwordDestroyTick = currentTick;
            }
        }

        return events;
    }

    /**
     * Tick pressure plates: check if any player is standing on a KIWEN_LA tile.
     * Scans loaded chunks for KIWEN_LA tiles and toggles active state.
     */
    tickPressurePlates(players: SimulationPlayer[]): SimulationEvent[] {
        const events: SimulationEvent[] = [];
        const newActive = new Set<string>();

        // Build set of all player feet positions
        const playerFeetPositions = new Set<string>();
        for (const player of players) {
            if (player.hp <= 0) continue;
            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            const feetY = player.y + height - 1;
            // Check tile UNDER feet (standing ON the plate)
            playerFeetPositions.add(`${player.x},${feetY + 1}`);
        }

        // Scan loaded chunks for pressure plates
        this.worldData.forEachChunk((cx, cy, tiles) => {
            for (let row = 0; row < tiles.length; row++) {
                for (let col = 0; col < tiles[row].length; col++) {
                    if (tiles[row][col] === TileType.KIWEN_LA) {
                        const worldX = cx * CHUNK_SIZE + col;
                        const worldY = cy * CHUNK_SIZE + row;
                        const key = `${worldX},${worldY}`;
                        if (playerFeetPositions.has(key)) {
                            newActive.add(key);
                        }
                    }
                }
            }
        });

        // Emit events for state changes
        for (const key of newActive) {
            if (!this.worldData.pressurePlateActive.has(key)) {
                const [x, y] = key.split(',').map(Number);
                events.push({ type: 'pressurePlateChanged', x, y, active: true });
            }
        }
        for (const key of this.worldData.pressurePlateActive) {
            if (!newActive.has(key)) {
                const [x, y] = key.split(',').map(Number);
                events.push({ type: 'pressurePlateChanged', x, y, active: false });
            }
        }

        this.worldData.pressurePlateActive = newActive;
        return events;
    }

    /**
     * Tick command blocks: check if any player is standing on a KIWEN_LI tile.
     * If the block has stored speech, replay it (with cooldown).
     */
    tickCommandBlocks(players: SimulationPlayer[], currentTick: number): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;
            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            const feetY = player.y + height - 1;
            const tileUnderFeet = this.worldData.getTile(player.x, feetY + 1);

            if (tileUnderFeet === TileType.KIWEN_LI) {
                const blockX = player.x;
                const blockY = feetY + 1;
                const stored = this.worldData.getCommandBlockSpeech(blockX, blockY);
                if (!stored) continue;

                const key = `${blockX},${blockY}`;
                const lastReplay = this.worldData.commandBlockCooldown.get(key) ?? -999;
                if (currentTick - lastReplay < COMMAND_BLOCK_COOLDOWN_TICKS) continue;

                this.worldData.commandBlockCooldown.set(key, currentTick);
                events.push({
                    type: 'commandBlockSpeech',
                    x: blockX,
                    y: blockY,
                    words: stored.words,
                    voice: stored.voice,
                });
            }
        }

        return events;
    }

    /**
     * Tick thwomps: KIWEN_ANPA blocks fall when a player passes below within THWOMP_TRIGGER_RANGE tiles.
     */
    tickThwomps(players: SimulationPlayer[]): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        // Scan loaded chunks for thwomp blocks
        this.worldData.forEachChunk((cx, cy, tiles) => {
            for (let row = 0; row < tiles.length; row++) {
                for (let col = 0; col < tiles[row].length; col++) {
                    if (tiles[row][col] !== TileType.KIWEN_ANPA) continue;

                    const worldX = cx * CHUNK_SIZE + col;
                    const worldY = cy * CHUNK_SIZE + row;

                    // Already falling? Skip
                    if (this.worldData.hasFallingBlock(worldX, worldY)) continue;

                    // Check for SKY below (can't fall if grounded)
                    const belowTile = this.worldData.getTile(worldX, worldY + 1);
                    if (belowTile !== TileType.SKY) continue;

                    // Check for players below within trigger range
                    let triggered = false;
                    for (const player of players) {
                        if (player.hp <= 0) continue;
                        if (player.x !== worldX) continue;
                        // Player must be below the thwomp
                        if (player.y <= worldY) continue;
                        if (player.y - worldY <= THWOMP_TRIGGER_RANGE) {
                            triggered = true;
                            break;
                        }
                    }

                    if (triggered) {
                        this.worldData.addFallingBlock(worldX, worldY);
                    }
                }
            }
        });

        return events;
    }

    /**
     * Tick question blocks: detect head-bonk (player rising into KIWEN_SEME from below).
     * Spawn random food above the block, then mark it inert.
     */
    tickQuestionBlocks(players: SimulationPlayer[], currentTick: number): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;
            // Head tile is at player.y (top of sprite)
            const headY = player.y;
            const tileAboveHead = this.worldData.getTile(player.x, headY - 1);

            if (tileAboveHead === TileType.KIWEN_SEME) {
                const blockX = player.x;
                const blockY = headY - 1;

                if (this.worldData.isQuestionBlockInert(blockX, blockY)) continue;

                // Spawn random food above the ? block
                const aboveY = blockY - 1;
                const aboveTile = this.worldData.getTile(blockX, aboveY);
                if (aboveTile === TileType.SKY) {
                    const foodIndex = ((blockX * 31 + blockY * 17 + currentTick) >>> 0) % QUESTION_BLOCK_FOODS.length;
                    const food = QUESTION_BLOCK_FOODS[foodIndex];
                    this.worldData.setTile(blockX, aboveY, food);
                    this.worldData.addFallingBlock(blockX, aboveY);
                    events.push({ type: 'tileChanged', x: blockX, y: aboveY, tileType: food });
                }

                this.worldData.markQuestionBlockInert(blockX, blockY);
            }
        }

        return events;
    }

    /**
     * Tick thornbushes: damage players and creatures standing in KASI_UTALA tiles.
     */
    tickThornbushes(players: SimulationPlayer[], getTile: (x: number, y: number) => TileType): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;

            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            let inThorns = false;
            for (let dy = 0; dy < height; dy++) {
                if (getTile(player.x, player.y + dy) === TileType.KASI_UTALA) {
                    inThorns = true;
                    break;
                }
            }

            if (inThorns) {
                player.thornbushTickCounter++;
                if (player.thornbushTickCounter >= THORNBUSH_DAMAGE_INTERVAL) {
                    player.thornbushTickCounter = 0;
                    player.hp -= THORNBUSH_DAMAGE;
                    if (player.hp <= 0) {
                        player.hp = 0;
                        events.push({ type: 'death', playerId: player.id, deathX: player.x, deathY: player.y + height - 1 });
                    } else {
                        events.push({ type: 'damage', playerId: player.id, hp: player.hp, source: 'slash' as const });
                    }
                }
            } else {
                player.thornbushTickCounter = 0;
            }
        }

        return events;
    }

    /**
     * Tick shrub support: shrubs (KASI_IJO) that have no solid ground below them disappear.
     */
    tickShrubSupport(): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        this.worldData.chunks.forEach((chunk, key) => {
            const [cx, cy] = key.split(',').map(Number);
            for (let ly = 0; ly < CHUNK_SIZE; ly++) {
                for (let lx = 0; lx < CHUNK_SIZE; lx++) {
                    if (chunk[ly][lx] !== TileType.KASI_IJO) continue;
                    const worldX = cx * CHUNK_SIZE + lx;
                    const worldY = cy * CHUNK_SIZE + ly;
                    const belowTile = this.worldData.getTile(worldX, worldY + 1);
                    if (isPassable(belowTile) || isBodyPassable(belowTile)) {
                        // No solid ground — shrub disappears
                        this.worldData.setTile(worldX, worldY, TileType.SKY);
                        events.push({ type: 'tileChanged', x: worldX, y: worldY, tileType: TileType.SKY });
                    }
                }
            }
        });

        return events;
    }

    /**
     * Tick fruit hazards: KILI_LAWA (spiky fruit — damage + push) and
     * KILI_SOWELI (acorn — damage on overlap). Both are passable + gravity-affected.
     */
    tickFruitHazards(
        players: SimulationPlayer[],
        getTile: (x: number, y: number) => TileType,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;

            const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
            let inLawa = false;
            let inSoweli = false;
            let hazardY = -1;

            for (let dy = 0; dy < height; dy++) {
                const tile = getTile(player.x, player.y + dy);
                if (tile === TileType.KILI_LAWA) {
                    inLawa = true;
                    hazardY = player.y + dy;
                }
                if (tile === TileType.KILI_SOWELI) {
                    inSoweli = true;
                    hazardY = player.y + dy;
                }
            }

            // KILI_LAWA: damage on interval + push sideways
            if (inLawa) {
                player.kiliLawaTickCounter++;
                if (player.kiliLawaTickCounter >= KILI_LAWA_DAMAGE_INTERVAL) {
                    player.kiliLawaTickCounter = 0;
                    player.hp -= KILI_LAWA_DAMAGE;
                    if (player.hp <= 0) {
                        player.hp = 0;
                        events.push({ type: 'death', playerId: player.id, deathX: player.x, deathY: player.y + height - 1 });
                    } else {
                        events.push({ type: 'damage', playerId: player.id, hp: player.hp, source: 'slash' as const });
                    }
                }
                // Push player to the side if there's room
                const pushDir = player.facing !== 0 ? -player.facing : 1;
                const pushX = player.x + pushDir;
                let canPush = true;
                for (let dy = 0; dy < height; dy++) {
                    if (!isBodyPassable(getTile(pushX, player.y + dy))) {
                        canPush = false;
                        break;
                    }
                }
                if (canPush) {
                    player.x = pushX;
                }
            } else {
                player.kiliLawaTickCounter = 0;
            }

            // KILI_SOWELI: instant damage on overlap, destroy the acorn
            if (inSoweli && hazardY >= 0) {
                player.hp -= KILI_SOWELI_DAMAGE;
                // Consume the acorn tile on contact
                this.worldData.setTile(player.x, hazardY, TileType.SKY);
                this.worldData.removeFallingBlock(player.x, hazardY);
                events.push({ type: 'tileChanged', x: player.x, y: hazardY, tileType: TileType.SKY });
                if (player.hp <= 0) {
                    player.hp = 0;
                    events.push({ type: 'death', playerId: player.id, deathX: player.x, deathY: player.y + height - 1 });
                } else {
                    events.push({ type: 'damage', playerId: player.id, hp: player.hp, source: 'slash' as const });
                }
            }
        }

        return events;
    }

    /**
     * Tick slow heal (KILI_KULUPU): heals 1 HP every KILI_KULUPU_HEAL_INTERVAL ticks
     * until fruitHealRemaining reaches 0.
     */
    tickSlowHeal(players: SimulationPlayer[]): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.fruitHealRemaining <= 0) continue;
            if (player.hp <= 0) {
                player.fruitHealRemaining = 0;
                continue;
            }

            player.fruitHealTickCounter++;
            if (player.fruitHealTickCounter >= KILI_KULUPU_HEAL_INTERVAL) {
                player.fruitHealTickCounter = 0;
                if (player.hp < player.maxHp) {
                    player.hp = Math.min(player.hp + 1, player.maxHp);
                    player.fruitHealRemaining--;
                    events.push({ type: 'damage', playerId: player.id, hp: player.hp, source: 'slash' as const });
                } else {
                    // At full HP, stop healing
                    player.fruitHealRemaining = 0;
                }
            }
        }

        return events;
    }

    /**
     * Tick suffocation: damage players whose head is inside a solid block.
     * Solid blocks (not passable, not ladder, not one-way) suffocate players.
     */
    tickSuffocation(
        players: SimulationPlayer[],
        getTile: (x: number, y: number) => TileType,
    ): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        for (const player of players) {
            if (player.hp <= 0) continue;

            const headTile = getTile(player.x, player.y);

            if (isSuffocating(headTile)) {
                player.suffocationTickCounter++;
                if (player.suffocationTickCounter >= SUFFOCATION_DAMAGE_INTERVAL) {
                    player.suffocationTickCounter = 0;
                    player.hp -= SUFFOCATION_DAMAGE;
                    if (player.hp <= 0) {
                        player.hp = 0;
                        const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
                        events.push({ type: 'death', playerId: player.id, deathX: player.x, deathY: player.y + height - 1 });
                    } else {
                        events.push({ type: 'damage', playerId: player.id, hp: player.hp, source: 'drowning' });
                    }
                }
            } else {
                player.suffocationTickCounter = 0;
            }
        }

        return events;
    }

    /**
     * Split a KILI_ALE (cornucopia) into up to 5 random fruits in adjacent air tiles.
     */
    private splitCornucopia(x: number, y: number): SimulationEvent[] {
        const events: SimulationEvent[] = [];

        // Remove the cornucopia itself
        this.worldData.setTile(x, y, TileType.SKY);
        this.worldData.removeFallingBlock(x, y);
        events.push({ type: 'tileChanged', x, y, tileType: TileType.SKY });

        // Check up to 5 adjacent positions for air
        const candidates: { x: number; y: number }[] = [];
        const checkPositions = [
            { x, y: y - 1 },     // above
            { x: x - 1, y },     // left
            { x: x + 1, y },     // right
            { x, y: y + 1 },     // below
            { x, y },            // self (now SKY)
        ];
        for (const pos of checkPositions) {
            if (this.worldData.getTile(pos.x, pos.y) === TileType.SKY) {
                candidates.push(pos);
            }
        }

        // Spawn seed-based random fruits at each candidate
        for (let i = 0; i < candidates.length; i++) {
            const pos = candidates[i];
            const hash = ((x * 31 + y * 17 + i * 7) >>> 0) % CORNUCOPIA_FRUITS.length;
            const fruit = CORNUCOPIA_FRUITS[hash];
            this.worldData.setTile(pos.x, pos.y, fruit);
            this.worldData.addFallingBlock(pos.x, pos.y);
            events.push({ type: 'tileChanged', x: pos.x, y: pos.y, tileType: fruit });
        }

        return events;
    }

    /**
     * Check if a point overlaps a player's hitbox.
     */
    overlapsPlayer(x: number, y: number, player: SimulationPlayer): boolean {
        if (x !== player.x) return false;
        const height = player.isCrouching ? PLAYER_HEIGHT_CROUCHING : PLAYER_HEIGHT_STANDING;
        return y >= player.y && y < player.y + height;
    }
}
